/**
 * Landing Hero — Phone in Hand preset (FLYR-629).
 *
 * A payment traces a bezier arc across the photo, sprinkling translucent coins in
 * its wake, then lands in a white disc that stamps a check and holds before the
 * next lap. Ported from Amy's approved prototype (edu-header.html).
 *
 * Decorative only (the canvas is aria-hidden); the photo and navy overlay are the
 * no-JS fallback. heroCanvas.js owns the lifecycle: section sizing, retina
 * scaling, the desktop-only gate, the reduced-motion still frame, and teardown.
 *
 * Registered by _blocks/landingHeroPresets/phone-in-hand.twig, so a page only
 * downloads the animation belonging to the preset it renders.
 */
import {
    bezierPath,
    drawCheck,
    drawCoins,
    drawHead,
    drawTrail,
    loadCheckIcon,
    mountLandingHeroCanvas,
    sprinkleCoins,
} from './heroCanvas.js';

// Progress per frame, matching the comp: the trail takes ~200 frames to draw, the
// disc ~33 to open, then the arrived state holds for two seconds.
const TRAIL_SPEED = 0.005;
const DISC_SPEED = 0.03;
const HOLD_FRAMES = 120;

const NUM_COINS = 20;
const DISC_RADIUS_MIN = 5;
const DISC_GROWTH = 30;
const CHECK_SIZE = 30;

mountLandingHeroCanvas('phone-in-hand', (ctx, { repaintIfStatic }) => {
    const checkIcon = loadCheckIcon(repaintIfStatic);

    let pathAt = () => ({ x: 0, y: 0 });
    let coins = [];
    let progress = 0;
    let discProgress = 0;
    let holdFrames = 0;

    // The single arc the comp tuned to cross the open right side of this photo.
    function buildPath(width, height) {
        return bezierPath([
            [
                { x: width * 0.62, y: height * 0.73 - 50 },
                { x: width * 0.55, y: height * 1.0 - 50 },
                { x: width * 1.0, y: height * 0.6 - 50 },
                { x: width * 0.68, y: height * 0.35 - 50 },
            ],
        ]);
    }

    function drawArrival() {
        const point = pathAt(1);
        const radius = DISC_RADIUS_MIN + DISC_GROWTH * discProgress;

        ctx.save();
        ctx.shadowBlur = 15 * discProgress;
        ctx.shadowColor = `rgba(0, 0, 0, ${0.3 * discProgress})`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.restore();

        // The check grows into the disc once it is half open.
        const scale = (discProgress - 0.5) * 2;
        if (scale <= 0) {
            return;
        }
        ctx.save();
        ctx.translate(point.x, point.y);
        ctx.scale(scale, scale);
        drawCheck(ctx, checkIcon, CHECK_SIZE);
        ctx.restore();
    }

    return {
        resize(width, height) {
            pathAt = buildPath(width, height);
            coins = sprinkleCoins(NUM_COINS);
        },

        step() {
            if (progress < 1) {
                progress = Math.min(progress + TRAIL_SPEED, 1);
                return;
            }
            if (discProgress < 1) {
                discProgress = Math.min(discProgress + DISC_SPEED, 1);
                return;
            }
            holdFrames++;
            if (holdFrames > HOLD_FRAMES) {
                progress = 0;
                discProgress = 0;
                holdFrames = 0;
                coins = sprinkleCoins(NUM_COINS);
            }
        },

        // The still frame is the arrived one: trail complete, check stamped.
        finish() {
            progress = 1;
            discProgress = 1;
            holdFrames = 0;
        },

        render() {
            drawTrail(ctx, pathAt, progress);
            drawCoins(ctx, coins, pathAt, progress);

            if (progress >= 1) {
                drawArrival();
            } else if (progress > 0) {
                drawHead(ctx, pathAt(progress), DISC_RADIUS_MIN);
            }
        },
    };
});
